import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Target,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  X,
  CheckCircle,
  Clock,
  TrendingUp,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import { getGoals, saveGoal, updateGoal, deleteGoal } from "../utils/storage";
import { FinancialGoal } from "../types";
import Layout from "../components/Layout";
import BottomNavigation from "../components/BottomNavigation";

const Goals: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [goals, setGoals] = useState<FinancialGoal[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<FinancialGoal | null>(null);
  const [filter, setFilter] = useState<"all" | "active" | "completed">("all");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    targetAmount: "",
    currentAmount: "",
    targetDate: "",
    category: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const userGoals = getGoals(user.id);
    setGoals(userGoals);
  }, [user, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.targetAmount || !formData.targetDate) {
      alert("Please fill in all required fields");
      return;
    }

    const goal: FinancialGoal = {
      id: editingGoal?.id || Date.now().toString(),
      userId: user!.id,
      title: formData.title,
      description: formData.description,
      targetAmount: parseFloat(formData.targetAmount),
      currentAmount: parseFloat(formData.currentAmount) || 0,
      targetDate: new Date(formData.targetDate),
      category: formData.category,
      createdAt: editingGoal?.createdAt || new Date(),
      isCompleted: editingGoal?.isCompleted || false,
    };

    if (editingGoal) {
      updateGoal(goal);
      setGoals(goals.map((g) => (g.id === editingGoal.id ? goal : g)));
    } else {
      saveGoal(goal);
      setGoals([...goals, goal]);
    }

    // Limpar formulário e fechar modal
    setFormData({
      title: "",
      description: "",
      targetAmount: "",
      currentAmount: "",
      targetDate: "",
      category: "",
    });
    setShowAddModal(false);
    setEditingGoal(null);
  };

  const handleEdit = (goal: FinancialGoal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      description: goal.description,
      targetAmount: goal.targetAmount.toString(),
      currentAmount: goal.currentAmount.toString(),
      targetDate: new Date(goal.targetDate).toISOString().split("T")[0],
      category: goal.category,
    });
    setShowAddModal(true);
  };

  const handleDelete = (goalId: string) => {
    if (window.confirm("Are you sure you want to delete this goal?")) {
      deleteGoal(user!.id, goalId);
      setGoals(goals.filter((g) => g.id !== goalId));
    }
  };

  const handleToggleComplete = (goal: FinancialGoal) => {
    const updatedGoal = { ...goal, isCompleted: !goal.isCompleted };
    updateGoal(updatedGoal);
    setGoals(goals.map((g) => (g.id === goal.id ? updatedGoal : g)));
  };

  const handleAddProgress = (goal: FinancialGoal, amount: number) => {
    const newCurrentAmount = Math.min(
      goal.currentAmount + amount,
      goal.targetAmount
    );
    const updatedGoal = {
      ...goal,
      currentAmount: newCurrentAmount,
      isCompleted: newCurrentAmount >= goal.targetAmount,
    };
    updateGoal(updatedGoal);
    setGoals(goals.map((g) => (g.id === goal.id ? updatedGoal : g)));
  };

  const filteredGoals = goals.filter((goal) => {
    switch (filter) {
      case "active":
        return !goal.isCompleted;
      case "completed":
        return goal.isCompleted;
      default:
        return true;
    }
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const getDaysRemaining = (targetDate: Date) => {
    const today = new Date();
    const diffTime = new Date(targetDate).getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return "bg-green-500";
    if (progress >= 75) return "bg-blue-500";
    if (progress >= 50) return "bg-yellow-500";
    if (progress >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  if (!user) {
    return null;
  }

  return (
    <Layout
      title="Financial Goals"
      subtitle="Set and track your financial objectives"
    >
      {/* Filtros */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-6"
      >
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                filter === "all"
                  ? "bg-emerald-500 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              <Target className="w-4 h-4" />
              <span>All</span>
            </button>
            <button
              onClick={() => setFilter("active")}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                filter === "active"
                  ? "bg-blue-500 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>Active</span>
            </button>
            <button
              onClick={() => setFilter("completed")}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                filter === "completed"
                  ? "bg-emerald-500 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              <span>Completed</span>
            </button>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Goal</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Lista de metas */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="space-y-4"
      >
        <AnimatePresence>
          {filteredGoals.length > 0 ? (
            filteredGoals
              .sort(
                (a, b) =>
                  new Date(a.targetDate).getTime() -
                  new Date(b.targetDate).getTime()
              )
              .map((goal) => {
                const progress = (goal.currentAmount / goal.targetAmount) * 100;
                const daysRemaining = getDaysRemaining(goal.targetDate);

                return (
                  <motion.div
                    key={goal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className={`rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-6 hover:bg-white/15 transition-all duration-300 ${
                      goal.isCompleted ? "opacity-75" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
                            <Target className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              {goal.title}
                            </h3>
                            {goal.isCompleted && (
                              <span className="bg-emerald-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1 mt-1">
                                <CheckCircle className="w-3 h-3" />
                                <span>Completed</span>
                              </span>
                            )}
                          </div>
                        </div>
                        {goal.description && (
                          <p className="text-gray-300 text-sm mb-2">
                            {goal.description}
                          </p>
                        )}
                        {goal.category && (
                          <span className="inline-block bg-purple-500/20 text-purple-400 text-xs px-2 py-1 rounded-full">
                            {goal.category}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(goal)}
                          className="p-2 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-all duration-200"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(goal.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Barra de progresso */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-300">Progress</span>
                        <span className="text-sm font-medium text-white">
                          {progress.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(
                            progress
                          )}`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-sm text-gray-300 mt-1">
                        <span>{formatCurrency(goal.currentAmount)}</span>
                        <span>{formatCurrency(goal.targetAmount)}</span>
                      </div>
                    </div>

                    {/* Informações adicionais */}
                    <div className="flex justify-between items-center text-sm text-gray-300">
                      <span className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Goal: {formatDate(goal.targetDate)}</span>
                      </span>
                      <span
                        className={`flex items-center space-x-1 ${
                          daysRemaining < 0
                            ? "text-red-400"
                            : daysRemaining < 7
                            ? "text-yellow-400"
                            : "text-gray-300"
                        }`}
                      >
                        <Clock className="w-4 h-4" />
                        <span>
                          {daysRemaining < 0
                            ? "Vencida"
                            : daysRemaining === 0
                            ? "Hoje"
                            : `${daysRemaining} dias restantes`}
                        </span>
                      </span>
                    </div>

                    {/* Ações */}
                    {!goal.isCompleted && (
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => {
                            const amount = parseFloat(
                              prompt("Quanto você quer adicionar?") || "0"
                            );
                            if (amount > 0) {
                              handleAddProgress(goal, amount);
                            }
                          }}
                          className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white font-semibold text-sm py-2 px-4 rounded-xl transition-all duration-200 flex items-center space-x-2"
                        >
                          <TrendingUp className="w-4 h-4" />
                          <span>Add Amount</span>
                        </button>
                        <button
                          onClick={() => handleToggleComplete(goal)}
                          className="bg-white/10 text-gray-300 hover:bg-white/20 text-sm py-2 px-4 rounded-xl transition-all duration-200 flex items-center space-x-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Mark as Completed</span>
                        </button>
                      </div>
                    )}
                  </motion.div>
                );
              })
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 bg-gradient-to-r from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                No goal found
              </h3>
              <p className="text-gray-300 mb-8 text-lg">
                {filter !== "all"
                  ? "Tente ajustar os filtros"
                  : "Crie sua primeira meta financeira e comece a economizar!"}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddModal(true)}
                className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-6 h-6" />
                <span>Create First Goal</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Modal de adicionar/editar meta */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-8 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">
                  {editingGoal ? "Edit Goal" : "New Financial Goal"}
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Goal Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full py-3 px-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                    placeholder="Ex: Trip to Europe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full py-3 px-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 h-20 resize-none"
                    placeholder="Descreva sua meta..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Target Amount *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.targetAmount}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          targetAmount: e.target.value,
                        })
                      }
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                      placeholder="0,00"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Current Amount
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.currentAmount}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          currentAmount: e.target.value,
                        })
                      }
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                      placeholder="0,00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Target Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.targetDate}
                      onChange={(e) =>
                        setFormData({ ...formData, targetDate: e.target.value })
                      }
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full py-3 px-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                    placeholder="Ex: Viagem, Casa, Carro..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 py-3 px-4 bg-white/10 text-gray-300 hover:bg-white/20 rounded-xl font-medium transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all duration-200"
                  >
                    {editingGoal ? "Update" : "Create Goal"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNavigation />
    </Layout>
  );
};

export default Goals;
